/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { ILobasDocumentListData } from './lobas-document-list.data';
import { ILobasDocumentListProps } from './lobas-document-list.props.autogenerated';
import { ILobasLinkProps } from '../components/lobas-table';
import {
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick,
    getTelemetryObject
} from '@msdyn365-commerce-modules/utilities';
import { IGetFtpDocumentsData } from '../../actions/get-ftp-documents.action';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

export interface ILobasDocumentListViewProps extends ILobasDocumentListProps<ILobasDocumentListData> {
    lobasDocumentListContainer?: React.ReactNode;
    className?: string;
}

/**
 *
 * LobasDocumentList component
 * @extends {React.PureComponent<ILobasDocumentListProps<ILobasDocumentListData>>}
 */
class LobasDocumentList extends React.PureComponent<ILobasDocumentListProps<ILobasDocumentListData>> {
    public render(): JSX.Element | null {
        const viewProps = {
            ...this.props,
            lobasDocumentListContainer: this._createLinkContainer(this.props.data.documents.result),
            className: this.props.config.className
        } as ILobasDocumentListViewProps;

        return this.props.renderView(viewProps);
    }

    private _createLinkContainer(documents: IGetFtpDocumentsData[] | undefined): JSX.Element | null {
        const links =
            documents &&
            documents
                .filter(
                    document =>
                        document.name !== '.' &&
                        document.name !== '..' &&
                        ((ArrayExtensions.hasElements(this.props.config.documentSearchKeywords) &&
                            this.props.config.documentSearchKeywords?.filter(keyword => document.name.startsWith(keyword)).length > 0) ||
                            !ArrayExtensions.hasElements(this.props.config.documentSearchKeywords))
                )
                .map((document, index) => {
                    const link = this._createLink(document);
                    return link;
                });

        return <div className={'lobas-document-linkContainer'}>{links}</div>;
    }

    private _createLink(content: IGetFtpDocumentsData): JSX.Element | null {
        const baseUrl = `https://lob.as/${this.props.config.ftpServerPath}`;

        const link: ILobasLinkProps = {
            linkUrl: `${baseUrl}/${content.name}`,
            linkText: content.name,
            linkType: '',
            disabled: false
        };

        // Construct telemetry attribute to render
        const payLoad = getPayloadObject('click', getTelemetryContent(this.props), '', '');
        const linkText = link.linkText ? link.linkText : '';
        payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(getTelemetryContent(this.props), payLoad);
        const btnClass = 'lobas-link-btn__primary';

        const editableLink: Msdyn365.ILinksData = {
            ariaLabel: '',
            className: btnClass,
            linkText: link.linkText,
            linkUrl: link.linkUrl,
            openInNewTab: true,
            role: 'link',
            additionalProperties: attributes,
            onClick: onTelemetryClick(getTelemetryContent(this.props), payLoad, linkText)
        };

        return (
            <div className='lobas-document-link' key={content.name}>
                <Msdyn365.Link link={editableLink} />
            </div>
        );
    }
}

export function getTelemetryContent(props: ILobasDocumentListProps<ILobasDocumentListData>): ITelemetryContent {
    return getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
}

export default LobasDocumentList;
